import ExcelJS from "exceljs";

export const IMPORT_TEMPLATE_HEADERS = ["ユーザー名", "メールアドレス", "権限", "プロダクトアクセス権限"] as const;

export const IMPORT_ROLE_OPTIONS = ["管理者", "一般"] as const;

/** 「プロダクトアクセス権限」列で複数プロダクトを並べるときの区切り文字。全角「、」も受け付ける。 */
export const IMPORT_PRODUCT_DELIMITER = ",";
const IMPORT_PRODUCT_DELIMITER_LABEL = "「,」または「、」";

const TEMPLATE_SHEET_NAME = "users";
/** ドロップダウンの選択肢を置く参照用シート。プロダクト名に区切り文字が入っても壊れないよう範囲参照にする。 */
const OPTIONS_SHEET_NAME = "options";
const DATA_VALIDATION_ROW_LIMIT = 1000;

const TEMPLATE_SAMPLE_NAME = "Taro Yamada";
const TEMPLATE_SAMPLE_EMAIL = "taro.yamada@example.com";
const TEMPLATE_SAMPLE_ROLE = "一般";

const TEMPLATE_COLUMN_WIDTHS = [24, 36, 14, 36] as const;

const HEADER_FILL_COLOR = "4472C4";

type ListDataValidation = {
  type: "list";
  allowBlank: boolean;
  formulae: string[];
  /** false のときドロップダウンは候補の提示に留まり、一覧にない値（＝複数指定）の手入力を弾かない。 */
  showErrorMessage?: boolean;
  showInputMessage?: boolean;
  promptTitle?: string;
  prompt?: string;
};

const addListValidation = (
  worksheet: ExcelJS.Worksheet,
  range: string,
  formula: string,
  allowBlank: boolean,
  options?: Omit<Partial<ListDataValidation>, "type" | "allowBlank" | "formulae">,
): void => {
  const dataValidations = (worksheet as unknown as { dataValidations: { add: (range: string, dv: ListDataValidation) => void } }).dataValidations;
  dataValidations.add(range, { type: "list", allowBlank, formulae: [formula], ...options });
};

/**
 * ドロップダウンに並べる「プロダクトアクセス権限」の選択肢は、契約中プロダクトの productName 単体のみ。
 * Excel も Google スプレッドシートも入力規則は単一選択しかできないため、複数指定は区切り文字での入力に任せる。
 */
export const buildProductChoices = (productOptions: readonly string[]): string[] =>
  Array.from(new Set(productOptions.map((option) => option.trim()).filter((option) => option !== "")));

/** プロダクト名に区切り記号が含まれても壊れないよう、テンプレートの CSV は RFC4180 どおりにクォートする。 */
const escapeCsvField = (value: string): string => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

const toCsvLine = (fields: readonly string[]): string => fields.map(escapeCsvField).join(",");

const buildSampleRow = (productOptions: readonly string[]): string[] => [
  TEMPLATE_SAMPLE_NAME,
  TEMPLATE_SAMPLE_EMAIL,
  TEMPLATE_SAMPLE_ROLE,
  // 複数プロダクトを区切り文字でつなぐ記入例として、契約中のプロダクトをすべて並べる。
  buildProductChoices(productOptions).join(IMPORT_PRODUCT_DELIMITER),
];

export const buildExcelTemplateBuffer = async (productOptions: readonly string[]): Promise<ArrayBuffer> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(TEMPLATE_SHEET_NAME);

  const headerRow = worksheet.addRow([...IMPORT_TEMPLATE_HEADERS]);
  worksheet.addRow(buildSampleRow(productOptions));

  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, size: 16, color: { argb: "FFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL_COLOR } };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    worksheet.getColumn(colNumber).width = TEMPLATE_COLUMN_WIDTHS[colNumber - 1] ?? 20;
  });

  addListValidation(worksheet, `C2:C${DATA_VALIDATION_ROW_LIMIT}`, `"${IMPORT_ROLE_OPTIONS.join(",")}"`, false);

  const productChoices = buildProductChoices(productOptions);
  if (productChoices.length > 0) {
    const optionsSheet = workbook.addWorksheet(OPTIONS_SHEET_NAME);
    productChoices.forEach((choice, index) => {
      optionsSheet.getCell(index + 1, 1).value = choice;
    });
    optionsSheet.state = "hidden";
    // 一覧にない値（＝区切り文字でつないだ複数指定）を弾かないよう、エラー警告は出さずに入力ヒントだけ添える。
    addListValidation(worksheet, `D2:D${DATA_VALIDATION_ROW_LIMIT}`, `=${OPTIONS_SHEET_NAME}!$A$1:$A$${productChoices.length}`, true, {
      showErrorMessage: false,
      showInputMessage: true,
      promptTitle: IMPORT_TEMPLATE_HEADERS[3],
      prompt: `プロダクト名はドロップダウンの表記どおりに入力してください。複数指定する場合は ${IMPORT_PRODUCT_DELIMITER_LABEL} 区切りで並べます。（例: ${productChoices.join(
        IMPORT_PRODUCT_DELIMITER,
      )}）`,
    });
  }

  return workbook.xlsx.writeBuffer();
};

export const buildCsvTemplateString = (productOptions: readonly string[]): string => {
  const bom = "﻿";
  return `${bom}${toCsvLine(IMPORT_TEMPLATE_HEADERS)}\r\n${toCsvLine(buildSampleRow(productOptions))}\r\n`;
};

const triggerDownload = (fileName: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadExcelTemplate = async (productOptions: readonly string[]): Promise<void> => {
  const buffer = await buildExcelTemplateBuffer(productOptions);
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload("ユーザー取り込みテンプレート.xlsx", blob);
};

export const downloadCsvTemplate = (productOptions: readonly string[]): void => {
  const blob = new Blob([buildCsvTemplateString(productOptions)], { type: "text/csv;charset=utf-8;" });
  triggerDownload("ユーザー取り込みテンプレート.csv", blob);
};
